import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { showSuccess } from '../../shared/ui/appToast';
import { listServicePackages } from '../../appointments/infra/servicePackagesRepo';
import { fetchProductCombos, getCatalogRamSnapshotCache } from '../../catalog/infra-api';
import { usePromotionsReloadBus } from '../../promotions/public-api';
import { listPromotionsCache } from '../../promotions/public-api';
import { subscribeCatalogSynced } from '../../sync/core/catalogSyncListeners';
import type { PosCatalogItemTypeFilter } from '../domain/posTypes';
import {
  loadPosFavorites,
  parseRailSelection,
  railProductIdsForView,
  resolvePromoProductIds,
  savePosFavorites,
  type PosRailSelection,
  type PosRailView,
} from './posQuickAccessRail';
type RailSets = { favorites: Set<string>; combos: Set<string>; promos: Set<string> };
const EMPTY_SETS: RailSets = {
  favorites: new Set(),
  combos: new Set(),
  promos: new Set(),
};

type Params = {
  currentBranchId: string | null | undefined;
  selectedCategory: string | 'all' | null;
  /** Chips solo aparecen si tienen contenido / capability. */
  hasServicePackages: boolean;
  promotionsEnabled: boolean;
  setCategory: (category: string | 'all' | null) => void;
  setItemTypeFilter: (value: PosCatalogItemTypeFilter) => void;
  /** Resetea la búsqueda al cambiar de vista (igual que al elegir categoría). */
  clearSearch: () => void;
  /** true si hay que re-leer sets (focus/sync) */
  reloadNonce: number;
};

/**
 * Estado + datos del rail de acceso rápido del POS.
 * Resuelve los sets de ids por vista (favoritos del dispositivo, combos vía
 * RPC `list_product_combos`, ofertas vía cache local de promos) y devuelve la
 * allowlist que filtra la grilla.
 */
export function usePosQuickAccessRail({
  currentBranchId,
  selectedCategory,
  hasServicePackages,
  promotionsEnabled,
  setCategory,
  setItemTypeFilter,
  clearSearch,
  reloadNonce,
}: Params) {
  const [railView, setRailView] = useState<PosRailSelection>('all');
  const [sets, setSets] = useState<RailSets>(EMPTY_SETS);
  const [packageCount, setPackageCount] = useState(0);
  const [catalogSyncNonce, setCatalogSyncNonce] = useState(0);
  const promotionsReloadNonce = usePromotionsReloadBus((s) => s.nonce);
  const favLoadedForRef = useRef<string | null>(null);

  useEffect(() => subscribeCatalogSynced(() => setCatalogSyncNonce((n) => n + 1)), []);

  // Favoritos: AsyncStorage por sucursal.
  useEffect(() => {
    if (!currentBranchId || favLoadedForRef.current === currentBranchId) return;
    let alive = true;
    favLoadedForRef.current = currentBranchId;
    void loadPosFavorites(currentBranchId).then((favs) => {
      if (alive) setSets((prev) => ({ ...prev, favorites: favs }));
    });
    return () => {
      alive = false;
    };
  }, [currentBranchId]);

  // Combos + ofertas: una pasada por sucursal / reloadNonce.
  useEffect(() => {
    if (!currentBranchId) {
      setSets(EMPTY_SETS);
      setRailView('all');
      return;
    }
    let alive = true;
    const rows = getCatalogRamSnapshotCache()?.products ?? [];
    const productIds = rows
      .map((p) => String(p.id))
      .filter(Boolean);

    const variantToProduct = new Map<string, string>();
    for (const v of getCatalogRamSnapshotCache()?.productVariants ?? []) {
      if (v.is_active === false) continue;
      variantToProduct.set(String(v.id), String(v.product_id));
    }

    const searchRows = rows.map((p) => ({
      id: String(p.id),
      categoryId: p.category_id != null ? String(p.category_id) : null,
      brandId: p.brand_id != null ? String(p.brand_id) : null,
    }));

    void Promise.all([
      fetchProductCombos(productIds).catch(() => new Map() as Map<string, unknown>),
      promotionsEnabled ? listPromotionsCache().catch(() => []) : Promise.resolve([]),
    ]).then(([combosMap, promos]) => {
      if (!alive) return;
      setSets((prev) => ({
        ...prev,
        combos: new Set([...combosMap.keys()].map(String)),
        promos: resolvePromoProductIds(promos, searchRows, variantToProduct, currentBranchId),
      }));
    });
    return () => {
      alive = false;
    };
  }, [currentBranchId, promotionsEnabled, reloadNonce, catalogSyncNonce, promotionsReloadNonce]);

  // Paquetes vendibles en POS (misma fuente que la grilla en vista package).
  useEffect(() => {
    if (!hasServicePackages || !currentBranchId) {
      setPackageCount(0);
      setRailView((v) => (v === 'packages' ? 'all' : v));
      return;
    }
    let alive = true;
    void listServicePackages(false, currentBranchId)
      .then((pkgs) => {
        if (!alive) return;
        const n = pkgs.length;
        setPackageCount(n);
        if (n === 0) setRailView((v) => (v === 'packages' ? 'all' : v));
      })
      .catch(() => {
        if (alive) setPackageCount(0);
      });
    return () => {
      alive = false;
    };
  }, [currentBranchId, hasServicePackages, reloadNonce, catalogSyncNonce]);

  // Mantener el chip de categoría alineado con el picker de filtros.
  useEffect(() => {
    if (selectedCategory != null && selectedCategory !== 'all') {
      const catSel = `cat:${selectedCategory}` as PosRailSelection;
      setRailView((prev) => (prev === catSel ? prev : catSel));
      return;
    }
    setRailView((prev) => (prev.startsWith('cat:') ? 'all' : prev));
  }, [selectedCategory]);

  const posProductIds = useMemo(
    () => railProductIdsForView(parseRailSelection(railView) as PosRailView, sets),
    [railView, sets],
  );

  const selectRail = useCallback(
    (sel: PosRailSelection) => {
      setRailView(sel);
      clearSearch();
      if (sel === 'packages') {
        setItemTypeFilter('package');
        setCategory('all');
        return;
      }
      setItemTypeFilter('all');
      if (sel.startsWith('cat:')) {
        setCategory(sel.slice(4));
      } else {
        setCategory('all');
      }
    },
    [clearSearch, setCategory, setItemTypeFilter],
  );

  const toggleFavorite = useCallback(
    (productId: string) => {
      if (!currentBranchId) return;
      const id = String(productId);
      setSets((prev) => {
        const next = new Set(prev.favorites);
        const added = !next.has(id);
        if (added) next.add(id);
        else next.delete(id);
        // Si se vació Favoritos estando en esa vista, volver a Todos.
        if (!added && next.size === 0) {
          setRailView((v) => (v === 'favorites' ? 'all' : v));
        }
        void savePosFavorites(currentBranchId, next);
        if (added && next.size === 1) {
          showSuccess('Favoritos', 'Los ves en la vista Favoritos, junto a Combos y Oferta.');
        }
        return { ...prev, favorites: next };
      });
    },
    [currentBranchId],
  );

  const isFavorite = useCallback((id: string) => sets.favorites.has(String(id)), [sets.favorites]);
  const isCombo = useCallback((id: string) => sets.combos.has(String(id)), [sets.combos]);
  const hasPromo = useCallback((id: string) => sets.promos.has(String(id)), [sets.promos]);

  return {
    railView,
    selectRail,
    posProductIds,
    counts: {
      favorites: sets.favorites.size,
      combos: sets.combos.size,
      promos: sets.promos.size,
      packages: packageCount,
    },
    toggleFavorite,
    isFavorite,
    isCombo,
    hasPromo,
  };
}
